import { createFileRoute } from "@tanstack/react-router";

import { CategoriaSeoPage, type CategoriaSeoConfig } from "@/components/site/CategoriaSeoPage";
import { productos } from "@/lib/productos";
import { crearHeadCategoriaSeo } from "@/lib/seo-categorias";

const modelos = productos.filter((producto) => producto.category === "personalizados");

const config: CategoriaSeoConfig = {
  path: "/stand-personalizado-nfc-qr",
  breadcrumb: "Stand personalizado NFC + QR",
  eyebrow: "Diseño personalizado NFC + QR",
  title: "Stand personalizado NFC + QR para negocios",
  intro:
    "Convierte el logo y los colores de tu negocio en un stand NFC + QR hecho a pedido. Lo configuramos con el enlace que necesites y lo entregamos probado.",
  category: "personalizados",
  featuredId: "stand-personalizado",
  benefits: [
    "Diseño con logo y colores del negocio",
    "NFC y código QR incluidos",
    "Enlace configurado antes de entregar",
    "Propuesta y alcance acordados contigo",
  ],
  sectionLabel: "Hecho para tu marca",
  sectionTitle: "Tu identidad en el mesón.",
  sectionCopy:
    "El stand personalizado combina una pieza física visible con un acceso digital directo. Antes de producirlo confirmamos el diseño, el enlace y el formato para que se ajuste al uso real de tu negocio.",
  useCases: [
    {
      titulo: "Reseñas y reputación",
      texto:
        "Dirige a Google para facilitar que clientes reales dejen una valoración después de comprar.",
    },
    {
      titulo: "Contacto y reservas",
      texto: "Abre WhatsApp, un formulario o la página donde recibes consultas y agendamientos.",
    },
    {
      titulo: "Redes sociales",
      texto:
        "Conecta con Instagram, TikTok u otro perfil manteniendo la identidad visual del local.",
    },
    {
      titulo: "Menú o catálogo",
      texto:
        "Lleva a una carta digital, catálogo, página de promociones o información del servicio.",
    },
  ],
  faqs: [
    {
      pregunta: "¿Qué se puede personalizar en el stand NFC?",
      respuesta:
        "El diseño puede incorporar el logo y los colores del negocio. Antes de producir confirmamos contigo la propuesta, el enlace y el alcance de la personalización.",
    },
    {
      pregunta: "¿Qué medidas tiene el stand personalizado?",
      respuesta: "El stand personalizado mide 15 cm de alto por 10 cm de ancho.",
    },
    {
      pregunta: "¿También ofrecen un adhesivo personalizado?",
      respuesta:
        "Sí. La categoría incluye un adhesivo personalizado NFC + QR de 10 × 10 cm para superficies compatibles.",
    },
    {
      pregunta: "¿Puedo elegir el enlace que abre?",
      respuesta:
        "Sí. Puede dirigir a Google, WhatsApp, redes sociales, un menú, un catálogo u otra página que acordemos antes de configurarlo.",
    },
    {
      pregunta: "¿Se entrega listo para usar?",
      respuesta:
        "Sí. Programamos el NFC y el QR con el enlace confirmado y comprobamos su funcionamiento antes de coordinar la entrega.",
    },
  ],
  finalTitle: "Cotiza tu diseño personalizado",
  finalCopy:
    "Envíanos tu logo, el enlace que quieres conectar y la cantidad. Te explicamos el alcance, el precio y la entrega antes de comenzar.",
  whatsappText:
    "Hola, vi el stand personalizado NFC + QR de Pyme Visible y quiero cotizar un diseño para mi negocio.",
};

export const Route = createFileRoute("/stand-personalizado-nfc-qr")({
  component: StandPersonalizadoNfcPage,
  head: () =>
    crearHeadCategoriaSeo({
      path: config.path,
      title: "Stands NFC + QR personalizados: modelos | Pyme Visible",
      description:
        "Cotiza un stand personalizado NFC + QR con el logo, colores y enlace de tu negocio. Configurado y listo para usar en Chile desde $30.000.",
      ogTitle: "Stand personalizado NFC + QR | Pyme Visible",
      ogDescription:
        "Diseño personalizado con logo, colores, NFC y QR para conectar el mesón de tu negocio.",
      image:
        modelos.find((producto) => producto.id === config.featuredId)?.image ??
        modelos[0]?.image ??
        "/productos/stand-google-negro.webp",
      breadcrumb: config.breadcrumb,
      products: modelos,
      faqs: config.faqs,
    }),
});

function StandPersonalizadoNfcPage() {
  return <CategoriaSeoPage config={config} />;
}
